import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook CRUD mínimo de áreas (produto/terreno comercializável).
 * Inclui palavras_chave (array) usadas pelo agent_detector_ofertas.
 */
export const useAreas = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAreas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('areas')
        .select('id, nome, descricao, palavras_chave, created_at')
        .order('nome', { ascending: true });

      if (err) throw err;
      setAreas(data || []);
    } catch (err) {
      console.error('Erro ao buscar áreas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAreas(); }, [fetchAreas]);

  const createArea = async (areaData) => {
    const payload = {
      nome: areaData.nome?.trim(),
      descricao: areaData.descricao?.trim() || null,
      palavras_chave: Array.isArray(areaData.palavras_chave)
        ? areaData.palavras_chave.filter(p => p && p.trim().length >= 2).map(p => p.trim())
        : [],
    };
    // Localização só entra no payload quando informada; caso contrário a coluna
    // é omitida e o default do banco (São Paulo/SP) ainda se aplica para callers
    // que não coletam cidade/UF (ex: criação inline de área na CreateOfertaModal).
    if (areaData.localizacao_cidade !== undefined) {
      payload.localizacao_cidade = areaData.localizacao_cidade?.trim() || null;
    }
    if (areaData.localizacao_uf !== undefined) {
      payload.localizacao_uf = areaData.localizacao_uf || null;
    }
    if (!payload.nome) throw new Error('Nome da área é obrigatório');

    const { data, error: err } = await supabase
      .from('areas')
      .insert(payload)
      .select()
      .single();

    if (err) throw err;
    await fetchAreas();
    return { success: true, area: data };
  };

  const updateArea = async (id, updates) => {
    const payload = { ...updates };
    if (payload.nome !== undefined) payload.nome = payload.nome?.trim();
    if (payload.descricao !== undefined) payload.descricao = payload.descricao?.trim() || null;
    if (payload.palavras_chave !== undefined) {
      payload.palavras_chave = Array.isArray(payload.palavras_chave)
        ? payload.palavras_chave.filter(p => p && p.trim().length >= 2).map(p => p.trim())
        : [];
    }

    const { data, error: err } = await supabase
      .from('areas')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (err) throw err;
    await fetchAreas();
    return { success: true, area: data };
  };

  const deleteArea = async (id) => {
    const { error: err } = await supabase
      .from('areas')
      .delete()
      .eq('id', id);
    if (err) throw err;
    await fetchAreas();
    return { success: true };
  };

  return { areas, loading, error, createArea, updateArea, deleteArea, refetch: fetchAreas };
};
